// Shared types + display helpers for the internship screens (InternshipManagement,
// InternshipDetailDialog, MyInternship, CertificateVerify). Shapes mirror DTOs/InternshipDtos.cs.

export interface InternshipSummary {
  id: number;
  userId: number;
  internName: string;
  internEmail: string;
  mentorId: number;
  mentorName: string;
  roleTitle: string;
  department: string | null;
  startDate: string;
  plannedEndDate: string;
  actualEndDate: string | null;
  durationMonths: number;
  status: string;
  extensionCount: number;
  currentCycle: number;
  daysRemaining: number;
  nextReviewDue: string | null;
  overdueReviews: number;
  latestProgressStatus: string | null;
}

export interface InternshipReview {
  id: number;
  reviewType: 'Monthly' | 'MidTerm' | 'Final';
  cycle: number;
  periodNo: number;
  dueDate: string;
  status: 'Pending' | 'Overdue' | 'Submitted' | 'Cancelled';
  selfAssessment: string | null;
  selfSubmittedAt: string | null;
  mentorRatings: Record<string, number> | null;
  overallRating: number | null;
  progressStatus: string | null;
  mentorFeedback: string | null;
  strengths: string | null;
  improvements: string | null;
  reviewedByName: string | null;
  mentorSubmittedAt: string | null;
  sharedWithIntern: boolean;
  canSubmitSelf: boolean;
  canSubmitMentor: boolean;
}

export interface InternshipDecision {
  id: number;
  cycle: number;
  goalScore: number | null;
  competencyScore: number | null;
  attendanceScore: number | null;
  attendancePercent: number | null;
  overallScore: number | null;
  suggestedOutcome: string | null;
  recommendation: string | null;
  recommendationReason: string | null;
  recommendedByName: string | null;
  recommendedAt: string | null;
  decisionReason: string | null;
  decidedByName: string | null;
  terminationReason: string | null;
  conversionSalary: number | null;
  decision: string | null;
  decidedAt: string | null;
  effectiveDate: string | null;
  extensionMonths: number | null;
  newEndDate: string | null;
  conversionDesignation: string | null;
  conversionEmploymentType: string | null;
}

export interface InternshipCertificate {
  id: number;
  certificateNo: string;
  certificateType: 'Completion' | 'Participation';
  verificationToken: string;
  verificationUrl: string;
  internName: string;
  roleTitle: string;
  department: string | null;
  startDate: string;
  endDate: string;
  issuedAt: string;
  emailSent: boolean;
  isRevoked: boolean;
  revokedAt: string | null;
  revokedReason: string | null;
  companyName: string;
  signatoryName: string;
  signatoryTitle: string;
}

export interface InternshipGoal {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  priority: string;
  weightage: number;
  progress: number;
  employeeComments: string | null;
  managerComments: string | null;
}

export interface InternshipScore {
  goalScore: number | null;
  competencyScore: number | null;
  attendanceScore: number | null;
  attendancePercent: number | null;
  overallScore: number | null;
  suggestedOutcome: string | null;
  goalWeight: number;
  competencyWeight: number;
  attendanceWeight: number;
}

export interface InternshipDetail extends InternshipSummary {
  college: string | null;
  stipend: number | null;
  notes: string | null;
  createdAt: string;
  shareUrl: string | null;
  viewerRole: 'Admin' | 'Mentor' | 'Manager' | 'Intern' | 'Public';
  finalWindowOpen: boolean;
  finalWindowOpensOn: string;
  canExtend: boolean;
  canRecommend: boolean;
  canDecide: boolean;
  canTerminate: boolean;
  canIssueCertificate: boolean;
  competencies: { key: string; label: string }[];
  reviews: InternshipReview[];
  decisions: InternshipDecision[];
  certificates: InternshipCertificate[];
  goals: InternshipGoal[];
  score: InternshipScore | null;
}

export interface InternshipDashboard {
  active: number;
  evaluationDue: number;
  underReview: number;
  endingIn30Days: number;
  overdueReviews: number;
  converted: number;
  completed: number;
  terminated: number;
  conversionRate: number | null;
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export const STATUS_META: Record<string, { label: string; color: ChipColor }> = {
  Active: { label: 'Active', color: 'primary' },
  EvaluationDue: { label: 'Evaluation due', color: 'warning' },
  UnderReview: { label: 'Awaiting HR decision', color: 'secondary' },
  Extended: { label: 'Extended', color: 'info' },
  Converted: { label: 'Confirmed', color: 'success' },
  Completed: { label: 'Completed', color: 'default' },
  Terminated: { label: 'Terminated', color: 'error' },
};

export const PROGRESS_META: Record<string, { label: string; color: ChipColor }> = {
  OnTrack: { label: 'On track', color: 'success' },
  NeedsAttention: { label: 'Needs attention', color: 'warning' },
  AtRisk: { label: 'At risk', color: 'error' },
};

export const REVIEW_STATUS_COLOR: Record<string, ChipColor> = {
  Pending: 'default',
  Overdue: 'error',
  Submitted: 'success',
  Cancelled: 'default',
};

export const OUTCOME_LABEL: Record<string, string> = {
  Confirm: 'Confirm (convert to employee)',
  Extend: 'Extend internship',
  Complete: 'Complete (release with certificate)',
  Terminate: 'Terminate early',
};

export const TERMINATION_REASONS = ['Performance', 'Misconduct', 'Voluntary', 'Other'];

// Employment type = what kind of engagement (User.EmploymentType, see backend EmploymentTypes),
// separate from the access Role. Hiring type = what a candidate is interviewed for.
export const EMPLOYMENT_TYPE_META: Record<string, { label: string; color: ChipColor }> = {
  Intern: { label: 'Intern', color: 'info' },
  Probation: { label: 'Probation', color: 'warning' },
  FullTime: { label: 'Full-time', color: 'success' },
  PartTime: { label: 'Part-time', color: 'default' },
  Contract: { label: 'Contract', color: 'secondary' },
};

export const HIRING_TYPES = ['Intern', 'FullTime', 'PartTime', 'Contract'];

export const reviewLabel = (r: Pick<InternshipReview, 'reviewType' | 'periodNo'>) =>
  r.reviewType === 'Final' ? 'Final evaluation' : r.reviewType === 'MidTerm' ? `Mid-term review (month ${r.periodNo})` : `Month ${r.periodNo} check-in`;

export const fmtDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const fmtScore = (value: number | null | undefined) => (value === null || value === undefined ? '—' : value.toFixed(2));

export const apiError = (err: any, fallback: string) => err?.response?.data?.message || fallback;
